import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, CheckCircle2, Star } from "lucide-react";
import { MaskedLines, FadeUp, CountUp } from "@/components/motion";
import { Container, SectionHeading } from "@/components/Section";
import PropertyCard from "@/components/PropertyCard";
import LeadForm from "@/components/LeadForm";
import useProperties from "@/hooks/useProperties";
import usePageMeta from "@/hooks/usePageMeta";
import { AREAS } from "@/data/content";

export default function AreaPage() {
  const { slug } = useParams();
  const area = AREAS.find((a) => a.slug === slug);
  const properties = useProperties();
  usePageMeta(area?.pageTitle, area?.metaDesc);

  if (!area) return <Navigate to="/" replace />;

  const related = area.relatedProperty
    ? properties.find((p) => p.url && p.url.includes(`/property/${area.relatedProperty}`))
    : null;

  return (
    <main data-testid={`area-page-${area.slug}`}>
      <section className="noise-dark bg-forest pb-24 pt-44 text-cream sm:pb-28 sm:pt-52">
        <Container className="relative z-10">
          <FadeUp>
            <p className="eyebrow flex items-center gap-2.5 text-sage">
              <MapPin className="h-4 w-4" /> Airbnb & short-term rental management · {area.name}
            </p>
          </FadeUp>
          <MaskedLines
            className="mt-6 max-w-4xl"
            lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.06]"
            lines={[
              <span key="1">{area.headline[0]}</span>,
              <span key="2" className="italic text-sage-light">{area.headline[1]}</span>,
            ]}
          />
          <FadeUp delay={0.35}>
            <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-cream/70">{area.intro}</p>
          </FadeUp>
          <FadeUp delay={0.5}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link
                to="/assessment"
                data-testid="area-hero-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
              >
                See What Your {area.name} Property Could Earn
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="area-hero-secondary"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/25 px-7 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:border-sage hover:text-sage-light"
              >
                How FieldGreen Works
              </Link>
            </div>
          </FadeUp>
        </Container>
      </section>

      <section className="bg-cream py-24 sm:py-32">
        <Container>
          <SectionHeading
            eyebrow={`Why ${area.name}`}
            title={`What drives short-term rental demand in ${area.name}.`}
          />
          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-forest/10 bg-forest/10 sm:grid-cols-2">
            {area.drivers.map((d, i) => (
              <FadeUp key={d.t} delay={i * 0.06} className="h-full">
                <div data-testid={`area-driver-${i}`} className="group flex h-full flex-col bg-cream p-9 transition-colors duration-500 hover:bg-forest">
                  <span className="font-mono text-xs text-forest/30 transition-colors duration-500 group-hover:text-cream/30">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-5 font-serif text-2xl font-medium text-forest transition-colors duration-500 group-hover:text-cream">{d.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65 transition-colors duration-500 group-hover:text-cream/65">{d.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <div className="mt-12 flex items-start gap-4 rounded-2xl border border-forest/12 bg-bone p-8">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-forest-700" />
              <p className="text-sm sm:text-base leading-relaxed text-charcoal/75" data-testid="area-property-types">
                <span className="font-semibold text-forest">What works here: </span>
                {area.types}
              </p>
            </div>
          </FadeUp>
        </Container>
      </section>

      {area.caseStudy && (
        <section className="bg-bone py-24 sm:py-28">
          <Container>
            <div className="grid items-start gap-8 lg:grid-cols-12">
              <FadeUp className="lg:col-span-5">
                <div className="tracing-card rounded-2xl bg-forest p-10 text-cream" data-testid="area-case-study">
                  <p className="eyebrow text-sage">A real FieldGreen property in {area.name}</p>
                  <p className="mt-6 font-mono text-5xl font-semibold text-cream">
                    <CountUp to={5800} prefix="€" />
                  </p>
                  <p className="mt-2 text-sm text-cream/65">owner earnings in August — from a 3-bed house in {area.name}</p>
                  <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-6">
                    <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 font-mono text-xs font-semibold text-gold">1.9×</span>
                    <p className="text-xs text-cream/50">vs €3,000/month as a long-term let. One month shown — revenue varies with season and demand.</p>
                  </div>
                </div>
              </FadeUp>
              {related && (
                <div className="lg:col-span-7">
                  <SectionHeading
                    eyebrow="See for yourself"
                    title={`This is what a managed ${area.name} property looks like.`}
                    copy="Real photos, real reviews, real bookings — guests can book it directly today."
                  />
                  <div className="mt-10 max-w-md">
                    <PropertyCard p={related} i={0} />
                  </div>
                </div>
              )}
            </div>
          </Container>
        </section>
      )}

      {!area.caseStudy && related && (
        <section className="bg-bone py-24 sm:py-28">
          <Container>
            <SectionHeading
              eyebrow="See for yourself"
              title={`A FieldGreen-managed property in ${area.name}.`}
              copy="Real photos, real reviews, real bookings — guests can book it directly today."
            />
            <div className="mt-12 max-w-md">
              <PropertyCard p={related} i={0} />
            </div>
          </Container>
        </section>
      )}

      <section className="noise-dark bg-forest py-24 text-cream sm:py-32">
        <Container className="relative z-10 grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <MaskedLines
              lineClassName="font-serif text-4xl sm:text-5xl font-medium leading-[1.1]"
              lines={[
                <span key="a">Own a property</span>,
                <span key="b" className="italic text-sage-light">in {area.name}?</span>,
              ]}
            />
            <FadeUp delay={0.3}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-cream/70">
                Tell us about it and we'll assess its realistic short-term rental performance in {area.name} —
                honest figures based on live local demand, within one working day.
              </p>
            </FadeUp>
            <FadeUp delay={0.4}>
              <div className="mt-8 flex items-center gap-2 text-xs text-cream/45" data-testid="area-rating-note">
                <Star className="h-3.5 w-3.5 fill-gold text-gold" />
                FieldGreen holds a 4.68 guest rating across 31 reviewed stays
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-6">
            <LeadForm source={`area-${area.slug}`} />
          </FadeUp>
        </Container>
      </section>
    </main>
  );
}
